"use client";

import { useState, useEffect, useContext, useMemo } from "react";
import { Plus, Edit2, Trash2, Eye, X, Search, Filter } from "lucide-react";
import {
  getPetsByOwner,
  deletePet,
  getPetById,
  getMedicalHistory,
} from "../../../services/api";
import { AuthContext } from "../../../context/AuthContext";
import AddPetModal from "./AddPetModal";
import EditPetModal from "./EditPetModal";
import CreateAppointmentModal from "./CreateAppointmentModal";

function MyPets() {
  const { loginUser, getPets, pets, petLoading, petError } =
    useContext(AuthContext);

  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(false);
  const [error, setError] = useState("");
  const [successMessage, setSuccessMessage] = useState("");
  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [showAppointmentModal, setShowAppointmentModal] = useState(false);
  const [selectedPet, setSelectedPet] = useState(null);
  const [selectedPetMedical, setSelectedPetMedical] = useState(null);
  const [editPet, setEditPet] = useState(null);
  const [appointmentPet, setAppointmentPet] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [filterSpecies, setFilterSpecies] = useState("");
  const [filterGender, setFilterGender] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 6;

  // Fetch pets when component mounts
  useEffect(() => {
    if (pets == null) {
      getPets();
    }
  }, [getPets, pets]);

  // Reset current page when filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm, filterSpecies, filterGender]);

  // Auto-clear messages after 5 seconds
  useEffect(() => {
    if (successMessage) {
      const timer = setTimeout(() => setSuccessMessage(""), 5000);
      return () => clearTimeout(timer);
    }
  }, [successMessage]);

  useEffect(() => {
    if (error) {
      const timer = setTimeout(() => setError(""), 5000);
      return () => clearTimeout(timer);
    }
  }, [error]);

  // Filter pets based on search term, species, and gender
  const filteredPets = useMemo(() => {
    return pets?.filter((pet) => {
      const matchesSearch =
        !searchTerm ||
        pet.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        pet.breed?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        pet.species?.toLowerCase().includes(searchTerm.toLowerCase());

      const matchesSpecies = !filterSpecies || pet.species === filterSpecies;
      const matchesGender = !filterGender || pet.gender === filterGender;

      return matchesSearch && matchesSpecies && matchesGender;
    });
  }, [pets, searchTerm, filterSpecies, filterGender]);

  // Paginate filtered pets
  const paginatedPets = useMemo(() => {
    const startIndex = (currentPage - 1) * itemsPerPage;
    const endIndex = startIndex + itemsPerPage;
    return filteredPets?.slice(startIndex, endIndex);
  }, [filteredPets, currentPage, itemsPerPage]);

  // Calculate total pages
  const totalPages = Math.ceil(filteredPets?.length / itemsPerPage);

  const handleDeletePet = async (petId) => {
    if (!window.confirm("Are you sure you want to delete this pet?")) {
      return;
    }

    try {
      setActionLoading(true);
      setError("");

      const response = await deletePet(petId);

      if (response.success) {
        setSuccessMessage("Pet deleted successfully!");
        await getPets(); // Refresh the list
      } else {
        setError(response.message || "Failed to delete pet");
      }
    } catch (err) {
      setError(err.message || "Failed to delete pet");
    } finally {
      setActionLoading(false);
    }
  };

  const handleViewDetails = async (pet) => {
    try {
      setActionLoading(true);
      setError("");

      // Fetch pet details
      const petResponse = await getPetById(pet._id);
      if (petResponse.success) {
        setSelectedPet(petResponse.data);

        // Fetch medical history
        try {
          const medicalResponse = await getMedicalHistory(pet._id);
          if (medicalResponse.success) {
            setSelectedPetMedical(medicalResponse.data);
          } else {
            setSelectedPetMedical(null);
          }
        } catch (medicalError) {
          console.error("Failed to fetch medical history:", medicalError);
          setSelectedPetMedical(null);
        }

        setShowDetailModal(true);
      } else {
        setError(petResponse.message || "Failed to fetch pet details");
      }
    } catch (err) {
      setError(err.message || "Failed to fetch pet details");
    } finally {
      setActionLoading(false);
    }
  };

  const openEditModal = (pet) => {
    setEditPet(pet);
    setShowEditModal(true);
  };

  const openAppointmentModal = (pet) => {
    setAppointmentPet(pet);
    setShowAppointmentModal(true);
  };

  const closeDetailModal = () => {
    setShowDetailModal(false);
    setSelectedPet(null);
    setSelectedPetMedical(null);
  };

  const closeAppointmentModal = () => {
    setShowAppointmentModal(false);
    setAppointmentPet(null);
  };

  const clearFilters = () => {
    setSearchTerm("");
    setFilterSpecies("");
    setFilterGender("");
    setCurrentPage(1);
  };

  const handleAddSuccess = (message) => {
    setSuccessMessage(message);
    getPets();
  };

  const handleEditSuccess = (message) => {
    setSuccessMessage(message);
    getPets();
  };

  const handleAppointmentSuccess = (message) => {
    setSuccessMessage(message);
    closeAppointmentModal();
  };

  const getHealthStatusColor = (status) => {
    switch (status?.toLowerCase()) {
      case "excellent":
        return "bg-green-100 text-green-800";
      case "good":
        return "bg-blue-100 text-blue-800";
      case "fair":
        return "bg-yellow-100 text-yellow-800";
      case "poor":
        return "bg-red-100 text-red-800";
      default:
        return "bg-gray-100 text-gray-800";
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return "N/A";
    return new Date(dateString).toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  };

  const getPetEmoji = (species) => {
    switch (species?.toLowerCase()) {
      case "dog":
        return "🐕";
      case "cat":
        return "🐱";
      case "bird":
        return "🦜";
      case "rabbit":
        return "🐰";
      case "hamster":
        return "🐹";
      case "fish":
        return "🐠";
      default:
        return "🐾";
    }
  };

  // Loading state for initial load
  if (petLoading && pets?.length === 0) {
    return (
      <div className="min-h-screen bg-gray-50 p-6">
        <div className="max-w-7xl mx-auto">
          <div className="flex items-center justify-center h-64">
            <div className="text-center">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
              <p className="text-gray-600 mt-4">Loading your pets...</p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="rounded-lg py-6">
      <div className="rounded-lg p-6 mb-6">
        {/* Header */}
        <div className="bg-gradient-to-r from-slate-50 to-blue-50 border border-gray-200 rounded-lg shadow-sm p-4 sm:p-6 mb-6">
          <div className="flex flex-row sm:items-center sm:justify-between gap-4">
            <div className="flex-1">
              <h1 className="text-2xl sm:text-3xl font-bold text-gray-900">
                My Pets
              </h1>
              <p className="text-gray-600 mt-1 text-sm sm:text-base">
                Manage your furry family members
              </p>
              {pets?.length > 0 && (
                <p className="text-xs sm:text-sm text-gray-500 mt-1">
                  Showing {filteredPets?.length} of {pets?.length} pets
                  {(searchTerm || filterSpecies || filterGender) &&
                    " (filtered)"}
                </p>
              )}
            </div>
            <div className="flex items-center space-x-3">
              <button
                onClick={() => setShowAddModal(true)}
                disabled={actionLoading}
                className="inline-flex items-center px-2.5 py-2.5 bg-blue-600 hover:bg-blue-700 text-white font-medium rounded-lg shadow-sm transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 hover:shadow-md"
              >
                <Plus className="w-4 h-4 sm:w-5 sm:h-5" />
                <span className="hidden sm:inline">Add New Pet</span>
              </button>
            </div>
          </div>
        </div>

        {/* Filters and Search */}
        <div className="rounded-lg shadow-sm mb-6">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <div className="relative">
              <Search className="w-5 h-5 absolute left-3 top-3 text-gray-400" />
              <input
                type="text"
                placeholder="Search pets..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>

            <select
              value={filterSpecies}
              onChange={(e) => setFilterSpecies(e.target.value)}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            >
              <option value="">All Species</option>
              <option value="Dog">Dogs</option>
              <option value="Cat">Cats</option>
              <option value="Bird">Birds</option>
              <option value="Rabbit">Rabbits</option>
              <option value="Hamster">Hamsters</option>
              <option value="Fish">Fish</option>
            </select>

            <select
              value={filterGender}
              onChange={(e) => setFilterGender(e.target.value)}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            >
              <option value="">All Genders</option>
              <option value="male">Male</option>
              <option value="female">Female</option>
            </select>

            <button
              onClick={clearFilters}
              disabled={!searchTerm && !filterSpecies && !filterGender}
              className="bg-gray-100 hover:bg-gray-200 text-gray-700 px-4 py-2 rounded-lg flex items-center space-x-2 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <Filter className="w-4 h-4" />
              <span>Clear Filters</span>
            </button>
          </div>
        </div>

        {/* Success Message */}
        {successMessage && (
          <div className="bg-green-50 border border-green-200 text-green-800 px-4 py-3 rounded-lg mb-6">
            <div className="flex justify-between items-center">
              <span>{successMessage}</span>
              <button
                onClick={() => setSuccessMessage("")}
                className="text-green-600 hover:text-green-800"
              >
                <X className="w-4 h-4" />
              </button>
            </div>
          </div>
        )}

        {/* Error Message */}
        {error && (
          <div className="bg-red-50 border border-red-200 text-red-800 px-4 py-3 rounded-lg mb-6">
            <div className="flex justify-between items-center">
              <span>{error}</span>
              <button
                onClick={() => setError("")}
                className="text-red-600 hover:text-red-800"
              >
                <X className="w-4 h-4" />
              </button>
            </div>
          </div>
        )}

        {/* Loading State for actions */}
        {actionLoading && (
          <div className="bg-blue-50 border border-blue-200 text-blue-800 px-4 py-3 rounded-lg mb-6">
            <div className="flex items-center space-x-2">
              <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-600"></div>
              <span>Processing...</span>
            </div>
          </div>
        )}

        {/* Pets Grid */}
        {paginatedPets?.length > 0 ? (
          <>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-6">
              {paginatedPets?.map((pet) => (
                <div
                  key={pet._id}
                  className="bg-white rounded-lg shadow-sm overflow-hidden hover:shadow-md transition-shadow"
                >
                  <div className="p-6">
                    <div className="flex items-center justify-between mb-4">
                      <div className="flex items-center space-x-3">
                        <div className="text-4xl">
                          {pet.image ? (
                            <div className="w-16 h-16 bg-gray-100 rounded-full overflow-hidden">
                              <img
                                src={
                                  pet.image ||
                                  `https://via.placeholder.com/150/e5e7eb/9ca3af?text=${
                                    pet.species?.charAt(0) || "P"
                                  }`
                                }
                                alt="Pet"
                                className="w-full h-full object-cover"
                              />


                            </div>
                          ) : (
                            getPetEmoji(pet.species)
                          )}
                        </div>
                        <div>
                          <h3 className="text-xl font-semibold text-gray-900">
                            {pet.name}
                          </h3>
                          <p className="text-gray-600">{pet.breed}</p>
                        </div>
                      </div>
                      <div className="flex space-x-2">
                        <button
                          onClick={() => handleViewDetails(pet)}
                          disabled={actionLoading}
                          className="p-2 text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-colors disabled:opacity-50"
                        >
                          <Eye className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => openEditModal(pet)}
                          disabled={actionLoading}
                          className="p-2 text-gray-400 hover:text-green-600 hover:bg-green-50 rounded-lg transition-colors disabled:opacity-50"
                        >
                          <Edit2 className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => handleDeletePet(pet._id)}
                          disabled={actionLoading}
                          className="p-2 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors disabled:opacity-50"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </div>

                    <div className="space-y-2">
                      <div className="flex items-center justify-between text-sm">
                        <span className="text-gray-500">Species:</span>
                        <span className="text-gray-900">{pet.species}</span>
                      </div>
                      <div className="flex items-center justify-between text-sm">
                        <span className="text-gray-500">Age:</span>
                        <span className="text-xs text-gray-500">
                          {(() => {
                            if (!pet.dateOfBirth) return "Unknown age";

                            const today = new Date();
                            const birthDate = new Date(pet.dateOfBirth);

                            // Difference in ms → days
                            const diffMs = today - birthDate;
                            const diffDays = Math.floor(
                              diffMs / (1000 * 60 * 60 * 24)
                            );
                            const diffMonths =
                              today.getMonth() -
                              birthDate.getMonth() +
                              12 *
                                (today.getFullYear() - birthDate.getFullYear());
                            const diffYears =
                              today.getFullYear() - birthDate.getFullYear();

                            // Adjust years properly
                            let ageYears = diffYears;
                            if (
                              today.getMonth() < birthDate.getMonth() ||
                              (today.getMonth() === birthDate.getMonth() &&
                                today.getDate() < birthDate.getDate())
                            ) {
                              ageYears--;
                            }

                            if (ageYears >= 1) {
                              return `${ageYears} year${
                                ageYears > 1 ? "s" : ""
                              } old`;
                            } else if (diffMonths >= 1) {
                              return `${diffMonths} month${
                                diffMonths > 1 ? "s" : ""
                              } old`;
                            } else {
                              return `${diffDays} day${
                                diffDays !== 1 ? "s" : ""
                              } old`;
                            }
                          })()}
                        </span>
                      </div>
                      <div className="flex items-center justify-between text-sm">
                        <span className="text-gray-500">Weight:</span>
                        <span className="text-gray-900">{pet.weight} kg</span>
                      </div>
                      <div className="flex items-center justify-between text-sm">
                        <span className="text-gray-500">Gender:</span>
                        <span className="text-gray-900 capitalize">
                          {pet.gender}
                        </span>
                      </div>
                    </div>

                    {pet.healthStatus && (
                      <div className="mt-4">
                        <span
                          className={`inline-block px-3 py-1 rounded-full text-xs font-medium ${getHealthStatusColor(
                            pet.healthStatus
                          )}`}
                        >
                          {pet.healthStatus}
                        </span>
                      </div>
                    )}

                    <div className="flex gap-2 mt-4">
                      <button
                        onClick={() => handleViewDetails(pet)}
                        className="flex-1 bg-gray-100 hover:bg-gray-200 text-gray-700 py-2 px-4 rounded-lg transition-colors duration-200 text-sm font-medium"
                      >
                        View Details
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            {/* Pagination */}
            {totalPages > 1 && (
              <div className="flex justify-center space-x-2">
                <button
                  onClick={() =>
                    setCurrentPage((prev) => Math.max(prev - 1, 1))
                  }
                  disabled={currentPage === 1}
                  className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Previous
                </button>

                {[...Array(totalPages)].map((_, index) => (
                  <button
                    key={index + 1}
                    onClick={() => setCurrentPage(index + 1)}
                    className={`px-4 py-2 border rounded-lg ${
                      currentPage === index + 1
                        ? "bg-blue-600 text-white border-blue-600"
                        : "border-gray-300 text-gray-700 hover:bg-gray-50"
                    }`}
                  >
                    {index + 1}
                  </button>
                ))}

                <button
                  onClick={() =>
                    setCurrentPage((prev) => Math.min(prev + 1, totalPages))
                  }
                  disabled={currentPage === totalPages}
                  className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Next
                </button>
              </div>
            )}
          </>
        ) : (
          !petLoading && (
            <div className="bg-white rounded-lg shadow-sm p-12 text-center">
              <div className="text-6xl mb-4">🐾</div>
              <h3 className="text-xl font-semibold text-gray-900 mb-2">
                {searchTerm || filterSpecies || filterGender
                  ? "No pets match your filters"
                  : "No pets found"}
              </h3>
              <p className="text-gray-600 mb-6">
                {searchTerm || filterSpecies || filterGender
                  ? "Try adjusting your search criteria or clear the filters."
                  : "You haven't added any pets yet."}
              </p>
              {searchTerm || filterSpecies || filterGender ? (
                <button
                  onClick={clearFilters}
                  className="bg-gray-600 hover:bg-gray-700 text-white px-6 py-3 rounded-lg transition-colors mr-4"
                >
                  Clear Filters
                </button>
              ) : (
                <button
                  onClick={() => setShowAddModal(true)}
                  className="bg-blue-600 hover:bg-blue-700 text-white px-6 py-3 rounded-lg transition-colors"
                >
                  Add Your First Pet
                </button>
              )}
            </div>
          )
        )}

        {/* Pet Details Modal */}
        {showDetailModal && selectedPet && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
            <div className="bg-white rounded-lg max-w-4xl w-full max-h-[90vh] overflow-y-auto">
              <div className="p-6">
                <div className="flex items-center justify-between mb-6">
                  <h2 className="text-2xl font-bold text-gray-900">
                    Pet Details
                  </h2>
                  <button
                    onClick={closeDetailModal}
                    className="text-gray-400 hover:text-gray-600"
                  >
                    <X className="w-6 h-6" />
                  </button>
                </div>

                <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                  <div>
                    <div className="text-center mb-6">
                      <div className="text-center mb-6">
                        <div className="mb-4">
                          {selectedPet.image ? (
                            <div className="w-32 h-32 bg-gray-100 rounded-full overflow-hidden mx-auto">
                              <img
                                src={
                                  selectedPet.image ||
                                  `https://via.placeholder.com/150/e5e7eb/9ca3af?text=${
                                    selectedPet.species?.charAt(0) || "P"
                                  }`
                                }
                                alt="Pet"
                                className="w-full h-full object-cover"
                              />
                            </div>
                          ) : (
                            <div className="text-8xl">
                              {getPetEmoji(selectedPet.species)}
                            </div>
                          )}
                        </div>
                        <h3 className="text-2xl font-bold text-gray-900">
                          {selectedPet.name}
                        </h3>
                        <p className="text-gray-600">{selectedPet.breed}</p>
                      </div>
                      {/* <h3 className="text-2xl font-bold text-gray-900">
                        {selectedPet.name}
                      </h3>
                      <p className="text-gray-600">{selectedPet.breed}</p> */}
                    </div>

                    <div className="space-y-4">
                      <div className="bg-gray-50 p-4 rounded-lg">
                        <h4 className="font-semibold text-gray-900 mb-3">
                          Basic Information
                        </h4>
                        <div className="space-y-2">
                          <div className="flex justify-between">
                            <span className="text-gray-600">Species:</span>
                            <span className="text-gray-900">
                              {selectedPet.species}
                            </span>
                          </div>
                          <div className="flex justify-between">
                            <span className="text-gray-600">Breed:</span>
                            <span className="text-gray-900">
                              {selectedPet.breed}
                            </span>
                          </div>
                          <div className="flex justify-between">
                            <span className="text-gray-600">Age:</span>
                            <span className="text-gray-900">
                              {(() => {
                                if (!selectedPet.dateOfBirth) return "Unknown";

                                const today = new Date();
                                const birthDate = new Date(
                                  selectedPet.dateOfBirth
                                );

                                // Difference in ms → days
                                const diffMs = today - birthDate;
                                const diffDays = Math.floor(
                                  diffMs / (1000 * 60 * 60 * 24)
                                );
                                const diffMonths =
                                  today.getMonth() -
                                  birthDate.getMonth() +
                                  12 *
                                    (today.getFullYear() -
                                      birthDate.getFullYear());
                                const diffYears =
                                  today.getFullYear() - birthDate.getFullYear();

                                // Adjust years correctly
                                let ageYears = diffYears;
                                if (
                                  today.getMonth() < birthDate.getMonth() ||
                                  (today.getMonth() === birthDate.getMonth() &&
                                    today.getDate() < birthDate.getDate())
                                ) {
                                  ageYears--;
                                }

                                if (ageYears >= 1) {
                                  return `${ageYears} year${
                                    ageYears > 1 ? "s" : ""
                                  }`;
                                } else if (diffMonths >= 1) {
                                  return `${diffMonths} month${
                                    diffMonths > 1 ? "s" : ""
                                  }`;
                                } else {
                                  return `${diffDays} day${
                                    diffDays !== 1 ? "s" : ""
                                  }`;
                                }
                              })()}
                            </span>
                          </div>
                          <div className="flex justify-between">
                            <span className="text-gray-600">Weight:</span>
                            <span className="text-gray-900">
                              {selectedPet.weight}kg
                            </span>
                          </div>
                          <div className="flex justify-between">
                            <span className="text-gray-600">Gender:</span>
                            <span className="text-gray-900 capitalize">
                              {selectedPet.gender}
                            </span>
                          </div>
                          <div className="flex justify-between">
                            <span className="text-gray-600">Color:</span>
                            <span className="text-gray-900">
                              {selectedPet.color}
                            </span>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>

                  <div>
                    <div className="space-y-6">
                      {/* Allergies */}
                      {selectedPetMedical?.allergies && (
                        <div className="bg-gray-50 p-4 rounded-lg">
                          <h4 className="font-semibold text-gray-900 mb-3">
                            Allergies & Medical History
                          </h4>
                          <p className="text-gray-700 text-sm">
                            {selectedPetMedical.allergies}
                          </p>
                        </div>
                      )}

                      {/* Vaccinations */}
                      <div className="bg-gray-50 p-4 rounded-lg">
                        <h4 className="font-semibold text-gray-900 mb-3">
                          Vaccinations
                        </h4>
                        {selectedPetMedical?.vaccinations &&
                        selectedPetMedical.vaccinations.length > 0 ? (
                          <div className="space-y-3">
                            {selectedPetMedical.vaccinations
                              .slice(0, 5)
                              .map((vaccination, index) => (
                                <div
                                  key={index}
                                  className="bg-white p-3 rounded border"
                                >
                                  <div className="font-medium text-gray-900">
                                    {vaccination.name}
                                  </div>
                                  <div className="text-sm text-gray-600">
                                    Date: {formatDate(vaccination.date)}
                                  </div>
                                </div>
                              ))}
                          </div>
                        ) : (
                          <p className="text-gray-500">
                            No vaccination records
                          </p>
                        )}
                      </div>

                      {/* Dewormings */}
                      <div className="bg-gray-50 p-4 rounded-lg">
                        <h4 className="font-semibold text-gray-900 mb-3">
                          Deworming History
                        </h4>
                        {selectedPetMedical?.dewormings &&
                        selectedPetMedical.dewormings.length > 0 ? (
                          <div className="space-y-3">
                            {selectedPetMedical.dewormings
                              .slice(0, 5)
                              .map((deworming, index) => (
                                <div
                                  key={index}
                                  className="bg-white p-3 rounded border"
                                >
                                  <div className="font-medium text-gray-900">
                                    {deworming.name}
                                  </div>
                                  <div className="text-sm text-gray-600">
                                    Date: {formatDate(deworming.date)}
                                  </div>
                                </div>
                              ))}
                          </div>
                        ) : (
                          <p className="text-gray-500">No deworming records</p>
                        )}
                      </div>

                      {/* Spayed/Neutered */}
                      <div className="bg-gray-50 p-4 rounded-lg">
                        <h4 className="font-semibold text-gray-900 mb-3">
                          Spay/Neuter Status
                        </h4>
                        {selectedPetMedical?.spayedNeutered &&
                        selectedPetMedical.spayedNeutered.length > 0 ? (
                          <div className="space-y-3">
                            {selectedPetMedical.spayedNeutered.map(
                              (entry, index) => (
                                <div
                                  key={index}
                                  className="bg-white p-3 rounded border"
                                >
                                  <div className="font-medium text-gray-900">
                                    Status:{" "}
                                    {entry.status === "yes"
                                      ? "Spayed/Neutered"
                                      : "Not Spayed/Neutered"}
                                  </div>
                                  {entry.status === "yes" && entry.date && (
                                    <div className="text-sm text-gray-600">
                                      Date: {formatDate(entry.date)}
                                    </div>
                                  )}
                                </div>
                              )
                            )}
                          </div>
                        ) : (
                          <p className="text-gray-500">
                            No spay/neuter information
                          </p>
                        )}
                      </div>
                    </div>
                  </div>
                </div>

                <div className="flex flex-wrap gap-3 mt-6 pt-6 border-t border-gray-200">
                  <button
                    onClick={() => {
                      closeDetailModal();
                      openEditModal(selectedPet);
                    }}
                    disabled={actionLoading}
                    className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg transition-colors disabled:opacity-50"
                  >
                    Edit Pet
                  </button>
                  {/* <button
                    disabled={actionLoading}
                    className="bg-purple-600 hover:bg-purple-700 text-white px-4 py-2 rounded-lg transition-colors disabled:opacity-50"
                  >
                    Add Medical Record
                  </button> */}
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Add Pet Modal */}
        <AddPetModal
          isOpen={showAddModal}
          onClose={() => setShowAddModal(false)}
          onSuccess={handleAddSuccess}
          onError={setError}
        />

        {/* Edit Pet Modal */}
        <EditPetModal
          isOpen={showEditModal}
          onClose={() => {
            setShowEditModal(false);
            setEditPet(null);
          }}
          onSuccess={handleEditSuccess}
          onError={setError}
          pet={editPet}
        />

        {/* Create Appointment Modal */}
        <CreateAppointmentModal
          isOpen={showAppointmentModal}
          onClose={closeAppointmentModal}
          onSuccess={handleAppointmentSuccess}
          onError={setError}
          selectedPet={appointmentPet}
        />
      </div>
    </div>
  );
}

export default MyPets;
